import { CANVAS_ZOOM } from "../canvas-controls.config";

/**
 * A viewport target: where a canvas should look, in one shape for every
 * caller — the URL hash, block links, and whatever moves the viewport next.
 *
 * - `x`, `y`: the point to center on, in canvas (flow) coordinates. Always
 *   present — a target is a point.
 * - `zoom`: the zoom to land at, clamped to the canvas range
 *   (CANVAS_ZOOM.minZoom..maxZoom). Absent keeps the current zoom.
 * - `duration`: the animation, in whole milliseconds. Absent or 0 jumps.
 *
 * `normalizeCanvasViewportTarget` is the single validation point: anything
 * that is not a finite number is dropped, a target without both `x` and `y`
 * is unusable (null), a zoom of 0 or less is dropped, a duration of 0 or
 * less is dropped and a fractional one is floored. It never throws.
 */
export interface CanvasViewportTarget {
  x: number;
  y: number;
  zoom?: number;
  duration?: number;
}

/** Loosely-typed fields as they arrive from a URL, a message or a caller. */
export type CanvasViewportTargetInput = {
  [K in keyof CanvasViewportTarget]?: unknown;
};

function finiteNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function normalizeCanvasViewportTarget(
  input: CanvasViewportTargetInput,
  {
    minZoom = CANVAS_ZOOM.minZoom,
    maxZoom = CANVAS_ZOOM.maxZoom,
  }: { minZoom?: number; maxZoom?: number } = {},
): CanvasViewportTarget | null {
  const x = finiteNumber(input.x);
  const y = finiteNumber(input.y);
  if (x === null || y === null) return null;
  const target: CanvasViewportTarget = { x, y };
  const zoom = finiteNumber(input.zoom);
  if (zoom !== null && zoom > 0) {
    target.zoom = Math.min(maxZoom, Math.max(minZoom, zoom));
  }
  const duration = finiteNumber(input.duration);
  if (duration !== null && duration > 0) target.duration = Math.floor(duration);
  return target;
}
